"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { X, Mail, Lock, User, CheckCircle, XCircle, Eye, EyeOff, UserPlus, LogIn } from "lucide-react"
import { loginUser, registerUser, checkUsernameAvailability, checkEmailAvailability } from "../api/authService"
import { Toast } from "./Toast"

interface LoginPromptModalProps {
  isOpen: boolean
  onClose: () => void
  onLoginSuccess: () => void
}

const LoginPromptModal: React.FC<LoginPromptModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const [mode, setMode] = useState<"prompt" | "login" | "signup">("prompt")
  const [isLoading, setIsLoading] = useState(false)
  const [loginFormData, setLoginFormData] = useState({
    username: "",
    password: "",
  })
  const [signupFormData, setSignupFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    display_name: "",
    isUsernameAvailable: false,
    isEmailAvailable: false,
  })

  // Reset mode when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMode("prompt")
      setLoginFormData({ username: "", password: "" })
      setSignupFormData({
        email: "",
        password: "",
        confirmPassword: "",
        display_name: "",
        isUsernameAvailable: false,
        isEmailAvailable: false,
      })
    }
  }, [isOpen])

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      const response = await loginUser(loginFormData.username, loginFormData.password)

      if (!response.success) {
        Toast.error(response.message || "Failed to sign in")
      } else {
        Toast.success("Welcome back!")
        onLoginSuccess()
      }
    } catch (error: any) {
      Toast.error(error.message || "Failed to sign in")
    } finally {
      setIsLoading(false)
    }
  }

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    if (signupFormData.password !== signupFormData.confirmPassword) {
      Toast.error("Passwords do not match")
      setIsLoading(false)
      return
    }

    if (!signupFormData.isUsernameAvailable) {
      Toast.error("Display name is not available. Please choose another one.")
      setIsLoading(false)
      return
    }

    if (!signupFormData.isEmailAvailable) {
      Toast.error("Email is already in use. Try logging in.")
      setIsLoading(false)
      return
    }

    try {
      const { success, message } = await registerUser({
        email: signupFormData.email,
        password: signupFormData.password,
        username: signupFormData.display_name,
      })

      if (!success) throw new Error(message)

      // Immediately log the user in
      const loginResponse = await loginUser(signupFormData.display_name, signupFormData.password)

      if (!loginResponse.success) throw new Error(loginResponse.message)

      Toast.success(
        "Registration successful! Please check your email for the verification link to verify your account!",
      )
      onLoginSuccess()
    } catch (error: any) {
      Toast.error(error.message || "Failed to register")
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3 }}
        className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-700 max-h-[90vh] overflow-y-auto"
      >
        <div className="bg-gradient-to-r from-gray-800 to-gray-700 p-6 relative border-b border-gray-600/50">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-300 hover:text-white transition-colors">
            <X className="h-6 w-6" />
          </button>
          <h2 className="text-xl font-bold text-white mb-1">
            {mode === "prompt" ? "Login Required" : mode === "login" ? "Welcome Back" : "Create Account"}
          </h2>
          <p className="text-sm text-gray-400">
            {mode === "prompt"
              ? "Please login or signup to share your testimony"
              : mode === "login"
                ? "Sign in to your account"
                : "Join our community today"}
          </p>
        </div>

        <div className="p-6">
          {mode === "prompt" && (
            <div className="space-y-4">
              <p className="text-gray-300 mb-6">
                You need to be logged in to share your testimony. Please login or create an account to continue.
              </p>
              <button
                onClick={() => setMode("login")}
                className="w-full py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg hover:from-amber-500 hover:to-orange-500 transition-all flex items-center justify-center space-x-2"
              >
                <LogIn className="w-5 h-5" />
                <span>Login</span>
              </button>
              <button
                onClick={() => setMode("signup")}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-500 hover:to-pink-500 transition-all flex items-center justify-center space-x-2"
              >
                <UserPlus className="w-5 h-5" />
                <span>Sign Up</span>
              </button>
            </div>
          )}

          {mode === "login" && (
            <form onSubmit={handleLoginSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">User Name</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    name="username"
                    value={loginFormData.username}
                    onChange={(e) => setLoginFormData({ ...loginFormData, username: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 rounded-lg border border-white/10 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 text-white"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="password"
                    name="password"
                    value={loginFormData.password}
                    onChange={(e) => setLoginFormData({ ...loginFormData, password: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 rounded-lg border border-white/10 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-white"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg hover:from-amber-500 hover:to-orange-500 transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <LogIn className="w-5 h-5" />
                <span>{isLoading ? "Signing in..." : "Sign In"}</span>
              </button>

              <p className="text-center text-gray-400 text-sm">
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={() => setMode("signup")}
                  className="text-orange-400 hover:text-orange-300"
                >
                  Sign up
                </button>
              </p>
            </form>
          )}

          {mode === "signup" && (
            <form onSubmit={handleSignupSubmit} className="space-y-6">
              <DisplayNameInput
                value={signupFormData.display_name}
                onChange={(e: any) => setSignupFormData({ ...signupFormData, [e.target.name]: e.target.value })}
              />

              <EmailInput
                value={signupFormData.email}
                onChange={(e: any) => setSignupFormData({ ...signupFormData, [e.target.name]: e.target.value })}
              />

              <PasswordFields formData={signupFormData} setFormData={setSignupFormData} />

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-500 hover:to-pink-500 transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <UserPlus className="w-5 h-5" />
                <span>{isLoading ? "Creating account..." : "Create Account"}</span>
              </button>

              <p className="text-center text-gray-400 text-sm">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className="text-orange-400 hover:text-orange-300"
                >
                  Sign in
                </button>
              </p>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  )
}



const DisplayNameInput = ({ value, onChange }: { value: string; onChange: (e: any) => void }) => {
  const [username, setUsername] = useState(value || "")
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (username.length < 4) {
      setIsAvailable(null)
      setSuggestions([])
      return
    }

    const delay = setTimeout(async () => {
      setLoading(true)
      const available = await checkUsernameAvailability(username)
      setIsAvailable(available)
      setLoading(false)

      if (!available) {
        generateSuggestions(username)
      } else {
        setSuggestions([])
      }
    }, 500)

    return () => clearTimeout(delay)
  }, [username])

  useEffect(() => {
    if (username.length >= 4 && isAvailable === true) {
      onChange({ target: { name: "isUsernameAvailable", value: true } })
    } else {
      onChange({ target: { name: "isUsernameAvailable", value: false } })
    }
  }, [username, isAvailable])

  const generateSuggestions = (base: string) => {
    const randomNumber = () => Math.floor(Math.random() * 1000)
    setSuggestions([`${base}${randomNumber()}`, `${base}_${randomNumber()}`, `${base}${randomNumber()}xyz`])
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "")
    setUsername(newValue)
    onChange({ target: { name: "display_name", value: newValue } })
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">Display Name</label>
      <div className="relative">
        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          name="display_name"
          value={username}
          onChange={handleChange}
          className="w-full pl-10 pr-10 py-3 bg-white/5 rounded-lg border border-white/10 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 text-white"
          required
        />
        {username.length > 0 && username.length < 4 ? (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-yellow-500 text-xs">Too short!</div>
        ) : loading ? (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">Checking...</div>
        ) : (
          isAvailable !== null && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {isAvailable ? (
                <CheckCircle className="text-green-500 w-5 h-5" />
              ) : (
                <XCircle className="text-red-500 w-5 h-5" />
              )}
            </div>
          )
        )}
      </div>

      {username.length >= 4 && !isAvailable && suggestions.length > 0 && (
        <div className="mt-2 text-sm text-gray-400">
          <p className="mb-1">Try one of these:</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                className="px-3 py-1 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-all text-xs"
                onClick={() => {
                  setUsername(suggestion)
                  onChange({ target: { name: "display_name", value: suggestion } })
                  setIsAvailable(true)
                  setSuggestions([])
                }}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const EmailInput = ({ value, onChange }: { value: string; onChange: (e: any) => void }) => {
  const [email, setEmail] = useState(value || "")
  const [isValid, setIsValid] = useState(true)
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!email) {
      setIsValid(true)
      setIsAvailable(null)
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    setIsValid(emailRegex.test(email))

    if (!emailRegex.test(email)) {
      setIsAvailable(null)
      return
    }

    const delay = setTimeout(async () => {
      setLoading(true)
      const available = await checkEmailAvailability(email)
      setIsAvailable(available)
      setLoading(false)
    }, 500)

    return () => clearTimeout(delay)
  }, [email])

  useEffect(() => {
    if (email.length >= 4 && isAvailable === true && isValid === true) {
      onChange({ target: { name: "isEmailAvailable", value: true } })
    } else {
      onChange({ target: { name: "isEmailAvailable", value: false } })
    }
  }, [email, isAvailable, isValid])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    onChange({ target: { name: "email", value: e.target.value } })
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="email"
          name="email"
          value={email}
          onChange={handleChange}
          className="w-full pl-10 pr-10 py-3 bg-white/5 rounded-lg border border-white/10 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all duration-300 text-white"
          required
        />
        {loading ? (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">Checking...</div>
        ) : (
          isValid &&
          isAvailable !== null && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {isAvailable ? (
                <CheckCircle className="text-green-500 w-5 h-5" />
              ) : (
                <XCircle className="text-red-500 w-5 h-5" />
              )}
            </div>
          )
        )}
      </div>

      {!isValid && email.length > 0 && <p className="text-red-500 text-sm mt-1">Invalid email format.</p>}

      {isValid && isAvailable === false && <p className="text-red-500 text-sm mt-1">This email is already in use.</p>}
    </div>
  )
}

const PasswordFields = ({ formData, setFormData }: { formData: any; setFormData: any }) => {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [strength, setStrength] = useState("")

  const getPasswordStrength = (pass: string) => {
    if (pass.length < 6) return "Too short"
    if (pass.match(/[A-Z]/) && pass.match(/[0-9]/) && pass.match(/[@$!%*?&#]/)) {
      return "Strong"
    }
    if (pass.match(/[A-Z]/) || pass.match(/[0-9]/) || pass.match(/[@$!%*?&#]/)) {
      return "Medium"
    }
    return "Weak"
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev: any) => ({ ...prev, [name]: value }))

    if (name === "password") {
      setStrength(getPasswordStrength(value))
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            onChange={handleChange}
            className="w-full pl-10 pr-10 py-3 bg-white/5 rounded-lg border border-white/10 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-white"
            required
            minLength={6}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        {formData.password && (
          <p
            className={`mt-2 text-sm ${
              strength === "Strong" ? "text-green-500" : strength === "Medium" ? "text-yellow-500" : "text-red-500"
            }`}
          >
            {strength}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type={showConfirmPassword ? "text" : "password"}
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className="w-full pl-10 pr-10 py-3 bg-white/5 rounded-lg border border-white/10 focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent transition-all duration-300 text-white"
            required
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        {formData.confirmPassword && (
          <p
            className={`mt-2 text-sm flex items-center ${
              formData.password === formData.confirmPassword ? "text-green-500" : "text-red-500"
            }`}
          >
            {formData.password === formData.confirmPassword ? (
              <>
                <CheckCircle className="w-4 h-4 mr-1" />
                Passwords match
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 mr-1" />
                Passwords do not match
              </>
            )}
          </p>
        )}
      </div>
    </div>
  )
}

export default LoginPromptModal
